import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import {
  Specialization_DifficultyLevel as DifficultyLevel,
  CareerRole_PathwayLevel as PathwayLevel,
} from '__generated__/graphql-types';

import RelatedCareerRolesCollectionQuery from 'bundles/unified-career-academy/queries/RelatedCareerRolesQuery.graphql';
import type {
  RelatedCareerRolesCollectionFragment,
  RelatedCareerRolesQueryQuery,
  RelatedCareerRolesQueryQueryVariables,
} from 'bundles/unified-career-academy/queries/__generated__/RelatedCareerRolesQuery';
import type { RelatedCareerRolesCollectionType } from 'bundles/unified-career-academy/types/relatedCareerRoles';

const RECOMMENDER_CONFIGURATION_ID = 'related-roles';

const getPathwayLevel = (difficultyLevel?: DifficultyLevel) => {
  switch (difficultyLevel) {
    case DifficultyLevel.Beginner:
      return PathwayLevel.Beginner;
    case DifficultyLevel.Intermediate:
      return PathwayLevel.Intermediate;
    case DifficultyLevel.Advanced:
      return PathwayLevel.Advanced;
    default:
      return undefined;
  }
};

const normalizeCareerRolesCollection = (
  content?: RelatedCareerRolesCollectionFragment
): RelatedCareerRolesCollectionType | undefined => {
  if (!content) {
    return undefined;
  }

  const collection = {
    ...content,
    entities: content?.entities?.elements
      .map((entity) => {
        if (!entity || entity.__typename !== 'Skill_CareerRole') {
          return null;
        }

        const pathways = {
          ...entity?.pathways,
          id: entity?.pathways?.id ?? '',
          entities: entity?.pathways?.entities?.elements
            .map((pathway) => {
              if (
                pathway?.__typename !== 'ProductCard_ProductCard' ||
                pathway?.productTypeAttributes?.__typename !== 'ProductCard_Specialization'
              ) {
                return null;
              }

              const product = pathway?.productTypeAttributes?.canonical;

              return {
                ...product,
                proficiencyLevel: getPathwayLevel(product?.difficultyLevel),
              };
            })
            .filter(Boolean),
        };

        return {
          ...entity,
          pathways,
        };
      })
      .filter(Boolean),
  };

  return collection;
};

function useRelatedCareerRoles({
  ssr,
  skip,
  limit = 4,
  slug,
}: {
  ssr: boolean;
  skip?: boolean;
  limit?: number;
  slug?: string;
}) {
  const { data, loading } = useQuery<RelatedCareerRolesQueryQuery, RelatedCareerRolesQueryQueryVariables>(
    RelatedCareerRolesCollectionQuery,
    {
      variables: {
        queryCollectionId: RECOMMENDER_CONFIGURATION_ID,
        additionalInput: {
          role_slug: slug,
        },
        limit,
      },
      notifyOnNetworkStatusChange: true,
      ssr,
      skip: skip || !slug,
      errorPolicy: 'all' as const,
      context: { clientName: 'gatewayGql' },
      onError: (error) =>
        Sentry.captureException(error, {
          extra: {
            message: `Error fetching related Pathway career roles collection with the recommender ID "${RECOMMENDER_CONFIGURATION_ID}"`,
          },
        }),
    }
  );

  return {
    data: normalizeCareerRolesCollection(data?.Collection?.queryCollection),
    loading,
  };
}

export default useRelatedCareerRoles;
